/* ==========================================================================
   ODINS — Product gallery
   assets/odins-gallery.js

   Defines the <media-gallery> custom element, replacing Dawn's media-gallery.js.
   Keeps setActiveMedia(mediaId, prepend) so product-info.js keeps working.

   Every image stays in the gallery, variant images included. Choosing a
   variant scrolls to its image instead of hiding the others.
   ========================================================================== */

(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var MOBILE = function () {
    return window.matchMedia('(max-width: 749px)').matches;
  };

  /* ======================================================================
     Lightbox — one instance shared by every gallery on the page
     ====================================================================== */
  var Lightbox = {
    el: null,
    img: null,
    items: [],
    index: 0,
    scale: 1,
    tx: 0,
    ty: 0,
    maxZoom: 4,
    lastFocus: null,
    pointers: new Map(),
    pinchStart: null,
    dragStart: null,
    lastTap: 0,

    build: function () {
      if (this.el) return;

      var wrap = document.createElement('div');
      wrap.className = 'odn-lightbox';
      wrap.setAttribute('data-open', 'false');
      wrap.setAttribute('role', 'dialog');
      wrap.setAttribute('aria-modal', 'true');
      wrap.setAttribute('aria-label', 'Product image viewer');
      wrap.innerHTML =
        '<div class="odn-lightbox__stage" data-stage>' +
        '<img class="odn-lightbox__img" data-img alt="">' +
        '</div>' +
        '<button type="button" class="odn-lightbox__btn odn-lightbox__close" data-close aria-label="Close">' +
        '<svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18"/></svg></button>' +
        '<button type="button" class="odn-lightbox__btn odn-lightbox__prev" data-prev aria-label="Previous image">' +
        '<svg viewBox="0 0 24 24"><path d="M15 5l-7 7 7 7"/></svg></button>' +
        '<button type="button" class="odn-lightbox__btn odn-lightbox__next" data-next aria-label="Next image">' +
        '<svg viewBox="0 0 24 24"><path d="M9 5l7 7-7 7"/></svg></button>' +
        '<div class="odn-lightbox__hint">Pinch or scroll to zoom</div>' +
        '<div class="odn-lightbox__count" data-count></div>';

      document.body.appendChild(wrap);

      this.el = wrap;
      this.stage = wrap.querySelector('[data-stage]');
      this.img = wrap.querySelector('[data-img]');
      this.countEl = wrap.querySelector('[data-count]');

      var self = this;
      wrap.querySelector('[data-close]').addEventListener('click', function () { self.close(); });
      wrap.querySelector('[data-prev]').addEventListener('click', function () { self.go(-1); });
      wrap.querySelector('[data-next]').addEventListener('click', function () { self.go(1); });

      this.stage.addEventListener('wheel', function (e) { self.onWheel(e); }, { passive: false });
      this.stage.addEventListener('pointerdown', function (e) { self.onDown(e); });
      this.stage.addEventListener('pointermove', function (e) { self.onMove(e); });
      this.stage.addEventListener('pointerup', function (e) { self.onUp(e); });
      this.stage.addEventListener('pointercancel', function (e) { self.onUp(e); });

      document.addEventListener('keydown', function (e) { self.onKey(e); }, true);
    },

    open: function (items, index, maxZoom) {
      this.build();
      this.items = items;
      this.maxZoom = maxZoom || 4;
      this.lastFocus = document.activeElement;
      this.prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.el.setAttribute('data-open', 'true');
      this.show(index);
      this.el.querySelector('[data-close]').focus();

      var multi = items.length > 1;
      this.el.querySelector('[data-prev]').hidden = !multi;
      this.el.querySelector('[data-next]').hidden = !multi;
      this.countEl.hidden = !multi;
    },

    close: function () {
      if (!this.el) return;
      this.el.setAttribute('data-open', 'false');
      document.body.style.overflow = this.prevOverflow || '';
      this.reset();
      if (this.lastFocus && document.contains(this.lastFocus)) this.lastFocus.focus();
      this.lastFocus = null;
    },

    isOpen: function () {
      return this.el && this.el.getAttribute('data-open') === 'true';
    },

    show: function (i) {
      this.index = (i + this.items.length) % this.items.length;
      var item = this.items[this.index];
      this.img.src = item.src;
      this.img.alt = item.alt || '';
      this.countEl.textContent = this.index + 1 + ' / ' + this.items.length;
      this.reset();
    },

    go: function (dir) {
      if (this.items.length < 2) return;
      this.show(this.index + dir);
    },

    reset: function () {
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this.apply();
    },

    apply: function () {
      this.clamp();
      this.img.style.transform =
        'translate(' + this.tx + 'px,' + this.ty + 'px) scale(' + this.scale + ')';
      this.el.setAttribute('data-zoomed', this.scale > 1.01 ? 'true' : 'false');
    },

    clamp: function () {
      if (this.scale <= 1) {
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
        return;
      }
      var s = this.stage.getBoundingClientRect();
      var w = this.img.offsetWidth * this.scale;
      var h = this.img.offsetHeight * this.scale;
      var mx = Math.max(0, (w - s.width) / 2);
      var my = Math.max(0, (h - s.height) / 2);
      this.tx = Math.min(mx, Math.max(-mx, this.tx));
      this.ty = Math.min(my, Math.max(-my, this.ty));
    },

    /* Zoom keeping the point under (cx, cy) fixed. cx/cy are measured
       from the centre of the stage. */
    zoomAt: function (next, cx, cy) {
      next = Math.min(this.maxZoom, Math.max(1, next));
      if (next === this.scale) return;
      this.tx = cx - (cx - this.tx) * (next / this.scale);
      this.ty = cy - (cy - this.ty) * (next / this.scale);
      this.scale = next;
      this.apply();
    },

    stageCoords: function (clientX, clientY) {
      var r = this.stage.getBoundingClientRect();
      return { x: clientX - r.left - r.width / 2, y: clientY - r.top - r.height / 2 };
    },

    onWheel: function (e) {
      e.preventDefault();
      var p = this.stageCoords(e.clientX, e.clientY);
      this.zoomAt(this.scale * Math.exp(-e.deltaY * 0.0016), p.x, p.y);
    },

    onDown: function (e) {
      this.stage.setPointerCapture(e.pointerId);
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this.pointers.size === 2) {
        var pts = Array.from(this.pointers.values());
        this.pinchStart = {
          dist: this.dist(pts[0], pts[1]),
          scale: this.scale
        };
        this.dragStart = null;
        return;
      }

      /* Double tap / double click toggles zoom */
      var now = Date.now();
      if (now - this.lastTap < 300) {
        var p = this.stageCoords(e.clientX, e.clientY);
        this.zoomAt(this.scale > 1.01 ? 1 : 2.5, p.x, p.y);
        this.lastTap = 0;
        this.dragStart = null;
        return;
      }
      this.lastTap = now;

      this.dragStart = { x: e.clientX, y: e.clientY, tx: this.tx, ty: this.ty, moved: 0 };
      this.el.setAttribute('data-dragging', 'true');
    },

    onMove: function (e) {
      if (!this.pointers.has(e.pointerId)) return;
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this.pointers.size === 2 && this.pinchStart) {
        var pts = Array.from(this.pointers.values());
        var d = this.dist(pts[0], pts[1]);
        var mid = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
        var p = this.stageCoords(mid.x, mid.y);
        this.zoomAt(this.pinchStart.scale * (d / this.pinchStart.dist), p.x, p.y);
        return;
      }

      if (!this.dragStart) return;
      var dx = e.clientX - this.dragStart.x;
      var dy = e.clientY - this.dragStart.y;
      this.dragStart.moved = Math.max(this.dragStart.moved, Math.abs(dx) + Math.abs(dy));

      if (this.scale > 1.01) {
        this.tx = this.dragStart.tx + dx;
        this.ty = this.dragStart.ty + dy;
        this.apply();
      }
    },

    onUp: function (e) {
      var start = this.dragStart;
      this.pointers.delete(e.pointerId);
      if (this.pointers.size < 2) this.pinchStart = null;
      this.el.setAttribute('data-dragging', 'false');

      if (start && this.scale <= 1.01) {
        var dx = e.clientX - start.x;
        var dy = e.clientY - start.y;
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          this.go(dx < 0 ? 1 : -1);
        }
      }
      if (this.pointers.size === 0) this.dragStart = null;
    },

    dist: function (a, b) {
      return Math.hypot(a.x - b.x, a.y - b.y);
    },

    onKey: function (e) {
      if (!this.isOpen()) return;
      if (e.key === 'Escape') { e.preventDefault(); this.close(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); this.go(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(-1); }
      else if (e.key === '+' || e.key === '=') { e.preventDefault(); this.zoomAt(this.scale * 1.4, 0, 0); }
      else if (e.key === '-') { e.preventDefault(); this.zoomAt(this.scale / 1.4, 0, 0); }
      else if (e.key === 'Tab') {
        /* Trap focus inside the dialog */
        var nodes = Array.prototype.slice
          .call(this.el.querySelectorAll('button:not([hidden])'))
          .filter(function (n) { return n.offsetParent !== null; });
        if (!nodes.length) return;
        var first = nodes[0];
        var last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
  };

  /* ======================================================================
     <media-gallery>
     ====================================================================== */
  class OdinsMediaGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-odn-viewport]');
      this.list = this.querySelector('[data-odn-list]');
      this.nav = this.querySelector('[data-odn-nav]');

      /* Dawn markup (e.g. a featured-product section that still uses the
         stock snippet) — degrade to the minimum contract and stop. */
      if (!this.viewport || !this.list) {
        this.legacy = true;
        return;
      }

      if (this.booted) {
        this.refresh();
        return;
      }
      this.booted = true;

      this.indicator = this.dataset.indicator || 'thumbs';
      this.maxZoom = parseFloat(this.dataset.maxZoom) || 4;
      this.lightboxEnabled = this.dataset.lightbox === 'true';
      this.activeIndex = 0;

      this.onNavClick = this.onNavClick.bind(this);
      this.onZoomClick = this.onZoomClick.bind(this);

      if (this.nav) this.nav.addEventListener('click', this.onNavClick);
      this.addEventListener('click', this.onZoomClick);

      this.refresh();
      this.openOnActive();

      var self = this;
      this.resizeObs = new ResizeObserver(function () { self.observeSlides(); });
      this.resizeObs.observe(this);
    }

    /* Arriving on a link to a particular variant, the mobile carousel
       starts on that variant's image rather than the first photo. Desktop
       is left alone: jumping the page on arrival would be disorienting. */
    openOnActive() {
      var active = this.list.querySelector('li[data-active="true"]');
      if (!active) return;
      var index = this.slides().indexOf(active);
      if (index <= 0) return;

      if (MOBILE()) {
        var self = this;
        window.requestAnimationFrame(function () {
          self.viewport.scrollTo({ left: active.offsetLeft - self.list.offsetLeft, behavior: 'auto' });
        });
      }
      this.setActiveIndex(index, false);
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.resizeObs) this.resizeObs.disconnect();
    }

    slides() {
      return Array.prototype.slice
        .call(this.list.querySelectorAll('li[data-media-id]'))
        .filter(function (li) { return !li.hidden; });
    }

    /* --- Indicators ------------------------------------------------- */
    refresh() {
      if (this.legacy) return;
      this.buildNav();
      this.observeSlides();
      this.setActiveIndex(this.activeIndex, false);
    }

    buildNav() {
      if (!this.nav) return;
      var slides = this.slides();
      this.nav.textContent = '';
      if (slides.length < 2) return;

      var frag = document.createDocumentFragment();
      var isThumbs = this.indicator === 'thumbs';

      slides.forEach(function (slide, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'odn-gallery__dot';
        b.setAttribute('role', 'tab');
        b.setAttribute('data-goto', String(i));
        b.setAttribute('aria-selected', 'false');
        b.setAttribute('aria-label', 'Go to image ' + (i + 1));

        if (isThumbs && slide.dataset.thumb) {
          var im = document.createElement('img');
          im.src = slide.dataset.thumb;
          im.alt = '';
          im.loading = 'lazy';
          b.appendChild(im);
        }
        frag.appendChild(b);
      });

      this.nav.appendChild(frag);
    }

    onNavClick(e) {
      var btn = e.target.closest('[data-goto]');
      if (!btn) return;
      this.scrollToIndex(parseInt(btn.dataset.goto, 10));
    }

    scrollToIndex(i) {
      var slides = this.slides();
      var slide = slides[i];
      if (!slide) return;

      if (MOBILE()) {
        this.viewport.scrollTo({
          left: slide.offsetLeft - this.list.offsetLeft,
          behavior: REDUCED ? 'auto' : 'smooth'
        });
      } else {
        slide.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'center' });
      }
      this.setActiveIndex(i, true);
    }

    setActiveIndex(i, scrollNav) {
      this.activeIndex = i;
      if (!this.nav) return;
      var dots = this.nav.querySelectorAll('[data-goto]');
      for (var d = 0; d < dots.length; d++) {
        var on = parseInt(dots[d].dataset.goto, 10) === i;
        dots[d].setAttribute('aria-selected', on ? 'true' : 'false');
        if (on && scrollNav && this.indicator === 'thumbs') {
          dots[d].scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
        }
      }
    }

    /* Track the active slide from scroll position rather than scroll events,
       so momentum scrolling on iOS doesn't desync the indicator. */
    observeSlides() {
      if (this.io) this.io.disconnect();
      if (!MOBILE()) return;

      var self = this;
      var slides = this.slides();

      this.io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var i = slides.indexOf(entry.target);
            if (i > -1 && i !== self.activeIndex) self.setActiveIndex(i, true);
          });
        },
        { root: this.viewport, threshold: 0.6 }
      );

      slides.forEach(function (s) { self.io.observe(s); });
    }

    /* --- Lightbox --------------------------------------------------- */
    onZoomClick(e) {
      if (!this.lightboxEnabled) return;
      var btn = e.target.closest('[data-odn-zoom]');
      if (!btn || btn.disabled) return;
      e.preventDefault();

      var slides = this.slides().filter(function (s) { return s.dataset.zoomSrc; });
      var current = btn.closest('li[data-media-id]');
      var start = slides.indexOf(current);

      Lightbox.open(
        slides.map(function (s) {
          return { src: s.dataset.zoomSrc, alt: s.dataset.alt || '' };
        }),
        start < 0 ? 0 : start,
        this.maxZoom
      );
    }

    /* --- Contract used by product-info.js --------------------------- */
    setActiveMedia(mediaId, prepend) {
      var target = this.querySelector('li[data-media-id="' + mediaId + '"]');
      if (!target) return;

      /* Nothing is hidden any more. Every image, including the ones that
         belong to other variants, stays in the gallery; choosing a variant
         simply brings its image into view. Clear any hidden attribute left
         over from an older render, just in case. */
      var stale = this.querySelectorAll('li[data-media-id][hidden]');
      for (var i = 0; i < stale.length; i++) stale[i].hidden = false;
      if (stale.length) this.refresh();

      var behaviour = prepend === false || REDUCED ? 'auto' : 'smooth';

      if (this.legacy) {
        target.scrollIntoView({ behavior: behaviour, block: 'nearest' });
        return;
      }

      var index = this.slides().indexOf(target);
      if (index < 0) return;

      if (MOBILE()) {
        /* The carousel slides sideways to the variant's image. */
        this.viewport.scrollTo({
          left: target.offsetLeft - this.list.offsetLeft,
          behavior: behaviour
        });
      } else if (!this.mostlyVisible(target)) {
        /* On desktop the page scrolls to the image, but only when it is
           not already on screen — scrolling to an image you can already
           see would just jolt the page. The product details column is
           sticky, so the variant picker stays in view while this happens. */
        target.scrollIntoView({ behavior: behaviour, block: 'start' });
      }

      this.setActiveIndex(index, true);
    }

    /* At least 60% of the image, or 60% of the screen for a tall one,
       visible below the sticky header. */
    mostlyVisible(el) {
      var r = el.getBoundingClientRect();
      var header = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height')
      ) || 0;
      var top = Math.max(r.top, header);
      var bottom = Math.min(r.bottom, window.innerHeight);
      var shown = Math.max(0, bottom - top);
      var needed = Math.min(r.height, window.innerHeight - header) * 0.6;
      return shown >= needed;
    }
  }

  if (!customElements.get('media-gallery')) {
    customElements.define('media-gallery', OdinsMediaGallery);
  }
})();